import { useMemo } from 'react';
import { type Optional } from 'utility-types';
import { type TriggerEventType } from '@bbkAdminComponents/play/play-trigger';
import {
  bbkApiSlice,
  type BBKRequestContextArgs,
} from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { DEFAULT_BRAND_ID } from '@bbkAdminRedux/rtkq/brands.slice';
import { type RestFunnel } from '@bbkAdminRedux/rtkq/funnels.slice';
import {
  type PublishState,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import { type TopPlayTargetingNode } from '@bbkAdminUtils/api-client/rest-routing-api';
import { type EnumLike } from '@bbkAdminUtils/utility-types';

type GetPlaysArgs = {
  appKey: string;
  brandId?: string;
  active?: boolean;
} & BBKRequestContextArgs;

type GetPlayArgs = {
  appKey: string;
  playId: string;
} & BBKRequestContextArgs;

type GetPlaysMetaDataArgs = {
  appKey: string;
  brandId?: string;
  playIds?: string[];
};

export type PlayTriggerType = {
  event_type?: TriggerEventType;
  expression?: string;
};

type UpdatePlayArgs = {
  appKey: string;
  playId: string;
  updatedPlay: PlayEdit;
};

type CreatePlayArgs = {
  appKey: string;
  body: PlayEdit;
};

type SaveDraftTargetingArgs = {
  appKey: string;
  playId: string;
  body: TopPlayTargetingNode;
};

type GetPlayGlobalConfigArgs = {
  appKey: string;
  brandId?: string;
};

type SavePlayGlobalConfigArgs = {
  appKey: string;
  brandId?: string;
  body: PlayGlobalConfig;
};

export type CancelConfig = {
  control_page: string;
  fallback_page: string;
  control_traffic_percent: number;
};

export type PlayGlobalConfig = {
  cancel_config: CancelConfig;
};

export enum PlayType {
  CHURN_PREVENTION = 'CHURN_PREVENTION',
  UPSELL = 'UPSELL',
  ACQUISITION = 'ACQUISITION',
  RETENTION = 'RETENTION',
  // Legacy, DON'T USE
  CHURN_DEFLECT = 'CHURN_DEFLECT',
}

export enum PlayPresentation {
  POP_UP = 'pop_up',
  MODAL = 'modal',
  BANNER = 'banner',
  CARD = 'card',
  PRICING_PAGE = 'pricing_page',
  CANCEL_PAGE = 'cancel_page',
}

export type Play = {
  active: boolean;
  enabled: boolean;
  application_id: string;
  created_at: string;
  id: string;
  modified_at: string;
  name: string;
  publish_state?: PublishState;
  brand_id?: string;
  play_type: EnumLike<typeof PlayType>;
  rule_id: string;
  updated_at: string;
  draft_targeting?: TopPlayTargetingNode;
  targeting?: TopPlayTargetingNode;
  play_trigger?: string;
  trigger_rules?: PlayTriggerType;
  priority?: number;
  presentation: EnumLike<typeof PlayPresentation>;
  isNew?: boolean;
};

export type PlayEdit = Optional<Play>;

export type PlayMeta = {
  entity_publish_state?: PublishState;
  is_play_launched?: boolean;
  play_id: string;
};

export type ContentPublishWarnings = {
  content: RestOffer;
  plays: Play[];
};

export type FunnelPublishWarnings = {
  funnel: RestFunnel;
  plays: Play[];
};

export type PlayPublishWarnings = {
  content_publish_warnings: ContentPublishWarnings[];
  funnel_publish_warnings: FunnelPublishWarnings[];
};

export const playSlice = bbkApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listPlays: builder.query<Play[], GetPlaysArgs>({
      query: (args) => ({
        url: `/api/v1/apps/${args.appKey}/plays`,
        params: {
          active_only: args?.active ?? true,
          brand_id: args.brandId,
          context: args.context,
        },
      }),
      providesTags: ['Plays'],
    }),
    updatePlay: builder.mutation<Play, UpdatePlayArgs>({
      query: ({ appKey, playId, updatedPlay }) => ({
        url: `/api/v1/apps/${appKey}/plays/${playId}`,
        method: 'PUT',
        body: updatedPlay,
      }),
      invalidatesTags: (result, error, args) => [
        'Plays',
        { type: 'Plays', id: args.playId },
        'FunnelListingMetadata',
      ],
    }),
    createPlay: builder.mutation<Play, CreatePlayArgs>({
      query: ({ appKey, body }) => ({
        url: `/api/v1/apps/${appKey}/plays`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Plays'],
    }),
    getPlay: builder.query<Play, GetPlayArgs>({
      query: ({ appKey, playId, context }) => ({
        url: `/api/v1/apps/${appKey}/plays/${playId}`,
        params: {
          context,
        },
      }),
      providesTags: (result, error, args) => [
        { type: 'Plays', id: args.playId },
      ],
    }),
    updatePlays: builder.mutation<Play, { appKey: string; body: PlayEdit[] }>({
      query: ({ appKey, body }) => ({
        url: `/api/v1/apps/${appKey}/plays/updatePlays`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, args) =>
        result
          ? [
              'Plays',
              ...args.body.map((play) => ({
                type: 'Plays' as const,
                id: play.id,
              })),
            ]
          : [],
    }),
    publishPlay: builder.mutation<void, GetPlayArgs>({
      query: ({ appKey, playId }) => ({
        url: `/api/v1/apps/${appKey}/plays/${playId}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, args) => [
        'Plays',
        { type: 'Plays', id: args.playId },
        'PlaysMetaData',
        'FunnelListingMetadata',
      ],
    }),
    saveDraftTargeting: builder.mutation<void, SaveDraftTargetingArgs>({
      query: ({ appKey, playId, body }) => ({
        url: `/api/v1/apps/${appKey}/plays/${playId}/targeting`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, args) => [
        'Plays',
        { type: 'Plays', id: args.playId },
        'PlaysMetaData',
      ],
    }),
    getPlaysMetaData: builder.query<PlayMeta[], GetPlaysMetaDataArgs>({
      query: ({ appKey, playIds, brandId }) => ({
        url: `/api/v1/apps/${appKey}/plays/meta`,
        method: 'GET',
        params: {
          brand_id: brandId,
          play_ids: playIds?.join(','),
        },
      }),
      providesTags: ['PlaysMetaData'],
    }),
    getPublishWarnings: builder.query<PlayPublishWarnings, GetPlayArgs>({
      query: (args) => ({
        url: `/api/v1/apps/${args.appKey}/plays/${args.playId}/warnings/publish`,
        method: 'GET',
      }),
    }),
    activatePlay: builder.mutation<void, GetPlayArgs>({
      query: ({ appKey, playId }) => ({
        url: `/api/v1/apps/${appKey}/plays/${playId}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, args) => [
        'Plays',
        { type: 'Plays', id: args.playId },
        'PlaysMetaData',
      ],
    }),
    getPlayGlobalConfig: builder.query<
      PlayGlobalConfig,
      GetPlayGlobalConfigArgs
    >({
      query: ({ appKey, brandId }) => ({
        url: `/api/v1/apps/${appKey}/play_global_config`,
        params: {
          brand_id:
            brandId && brandId !== DEFAULT_BRAND_ID ? brandId : undefined,
        },
      }),
      providesTags: ['CancelPlayConfig'],
    }),
    savePlayGlobalConfig: builder.mutation<
      PlayGlobalConfig,
      SavePlayGlobalConfigArgs
    >({
      query: ({ appKey, brandId, body }) => ({
        url: `/api/v1/apps/${appKey}/play_global_config`,
        method: 'PUT',
        params: {
          brand_id:
            brandId && brandId !== DEFAULT_BRAND_ID ? brandId : undefined,
        },
        body,
      }),
      invalidatesTags: ['CancelPlayConfig', 'FunnelListingMetadata'],
    }),
  }),
});

export const useGetPlay = (args: GetPlayArgs) => {
  const { data } = playSlice.endpoints.getPlay.useQuery(args);
  return useMemo(() => data ?? undefined, [data]);
};
